// Arrival scheduling and spawning: where a new arrival appears (edgePoint),
// what it flies (pickTypeId/pickSide/pickAim), the gate rule that keeps two
// arrivals from being promised the same stretch of edge or entering into a
// conflict the player has no time to solve (spaceGate/clearEntry/slideGate),
// spawnPlane itself, and the traffic governor (trafficCap). No tick-loop
// concerns (advance/collide/land) — those stay in world.ts's step().
import type { Aircraft, Scheduled, Vec } from './types.ts';
import type { World } from './world.ts';
import { aircraftType } from './aircraft.ts';
import { clamp } from './geometry.ts';
import { EDGE_MARGIN, GATE_CLEAR, GATE_LOOK, GATE_SEP, GATE_SLIDE } from './tuning.ts';

/** entry headings tried, as offsets from the arrival's own aim, least bend
 *  first — where it enters is promised, where it points is not */
const GATE_FAN = [0, 0.30, -0.30, 0.62, -0.62];

/** how far outside the viewport an arrival appears — and, on the radar, how
 *  far outside the mapped field its gate is drawn */
export function edgePoint(w: World, side: number, t: number): Vec {
  const m = EDGE_MARGIN;
  if (side === 0) return { x: t * w.W, y: -m };
  if (side === 1) return { x: w.W + m, y: t * w.H };
  if (side === 2) return { x: t * w.W, y: w.H + m };
  return { x: -m, y: t * w.H };
}

function pickTypeId(w: World): string {
  const mix = w.level.mix;
  let r = w.rng.next();
  for (const [id, wt] of mix) { r -= wt; if (r <= 0) return id; }
  return mix[mix.length - 1][0];
}

function pickSide(w: World): 0 | 1 | 2 | 3 {
  const ws = w.level.arrivalSides ?? [1, 1, 1, 1];
  const tot = ws[0] + ws[1] + ws[2] + ws[3];
  let r = w.rng.next() * tot;
  for (let i = 0; i < 4; i++) { r -= ws[i]; if (r <= 0) return i as 0 | 1 | 2 | 3; }
  return 3;
}

/** where a new arrival means to fly: a point in the middle of the field */
function pickAim(w: World): Vec {
  return { x: w.W * (0.3 + w.rng.next() * 0.4), y: w.H * (0.3 + w.rng.next() * 0.4) };
}

/**
 * Two arrivals must not be promised the same stretch of edge: the radar shows
 * both gates, the player clears one corridor, and the second aircraft enters
 * into the first. Push a freshly drawn gate off any gate already scheduled on
 * the same side — deterministically, so the randomness stays the level's.
 */
function spaceGate(w: World, side: 0 | 1 | 2 | 3, pos: number): number {
  const span = side === 0 || side === 2 ? w.W : w.H;
  const need = GATE_CLEAR / span;
  for (const s of w.schedule) {
    if (s.side !== side) continue;
    const d = pos - s.edgePos;
    if (Math.abs(d) < need) pos = s.edgePos + (d >= 0 ? need : -need);
  }
  return clamp(pos, 0.04, 0.96);
}

export function schedule(w: World, eta: number): Scheduled {
  const side = pickSide(w);
  const edgePos = spaceGate(w, side, w.rng.next());
  return { eta, side, edgePos, typeId: pickTypeId(w), aim: pickAim(w), slide: edgePos < 0.5 ? 1 : -1 };
}

// ---------- the gate rule ----------
//
// An arrival must never appear inside a conflict the player has no time to
// solve. The lab had already named these: a crash less than
// `STRUCTURAL_SPAWN_S` (3 s) after one of the two aircraft appeared is a loss
// no pilot, bot or human, had a reaction window for — the spawn governor's
// fault, not the player's.
//
// So an arrival enters through a *gate*: the point on the edge the radar has
// been showing it at. Before it comes through, the gate has to be clear and
// the track beyond it has to stay clear, and if neither can be arranged the
// arrival waits outside and walks along its edge until it finds room. What is
// never done is move the gate out from under the player: the bearing the radar
// promised is the bearing the aircraft arrives on.

/**
 * Closest the two straight tracks come over the next `T` seconds. Exact for
 * the aircraft that matter — an arrival flies straight until the player draws
 * on it, and so does anything else nobody has drawn on — and pessimistic for
 * the rest, which is the right way round for a guard.
 */
function closestApproach(
  ax: number, ay: number, avx: number, avy: number,
  bx: number, by: number, bvx: number, bvy: number, T: number,
): number {
  const rx = bx - ax, ry = by - ay, vx = bvx - avx, vy = bvy - avy;
  const vv = vx * vx + vy * vy;
  const t = vv > 1e-9 ? clamp(-(rx * vx + ry * vy) / vv, 0, T) : 0;
  return Math.hypot(rx + vx * t, ry + vy * t);
}

/** how much room an entry at (x,y) on `ang` would keep from live traffic */
function entryRoom(w: World, x: number, y: number, ang: number, speed: number): number {
  const vx = Math.cos(ang) * speed, vy = Math.sin(ang) * speed;
  let room = Infinity;
  for (const q of w.planes) {
    if (q.rollout > 0.6) continue;       // down and rolling out: out of the air
    // a holding aircraft orbits its own patch instead of going anywhere, so it
    // counts as parked; everything else is projected along its heading
    const qs = q.holding ? 0 : q.speed;
    const d = closestApproach(x, y, vx, vy, q.x, q.y, Math.cos(q.ang) * qs, Math.sin(q.ang) * qs, GATE_LOOK);
    if (d < room) room = d;
  }
  return room;
}

/** The heading this arrival may come through its gate on, or null if it may
 *  not come through at all yet. */
export function clearEntry(w: World, s: Scheduled): { x: number; y: number; ang: number } | null {
  const { x, y } = edgePoint(w, s.side, s.edgePos);
  for (const q of w.planes) {
    if (q.rollout > 0.6) continue;
    if (Math.hypot(q.x - x, q.y - y) < GATE_CLEAR) return null;
  }
  const speed = aircraftType(s.typeId).speed * w.level.tempo;
  const base = Math.atan2(s.aim.y - y, s.aim.x - x);
  let bestAng = base, bestRoom = -1;
  for (const off of GATE_FAN) {
    const room = entryRoom(w, x, y, base + off, speed);
    if (room > bestRoom) { bestRoom = room; bestAng = base + off; }
    if (bestRoom >= GATE_SEP) break;     // the least bend that works, wins
  }
  return bestRoom >= GATE_SEP ? { x, y, ang: bestAng } : null;
}

/** A held arrival walks along its own edge until it finds room, bouncing at
 *  the corners. The radar dot walks with it, so what the player is shown stays
 *  true, and an aircraft parked on a gate delays traffic instead of wedging it. */
export function slideGate(s: Scheduled, dt: number): void {
  s.edgePos += s.slide * GATE_SLIDE * dt;
  if (s.edgePos <= 0.04) { s.edgePos = 0.04; s.slide = 1; }
  else if (s.edgePos >= 0.96) { s.edgePos = 0.96; s.slide = -1; }
}

export function spawnPlane(w: World, s: Scheduled, entry: { x: number; y: number; ang: number }): Aircraft {
  const { x, y, ang } = entry;
  const type = aircraftType(s.typeId);
  const p: Aircraft = {
    id: w.nextId++,
    x, y, ang,
    type,
    speed: type.speed * (0.94 + w.rng.next() * 0.12) * w.level.tempo,
    path: [], pathSnapped: 0, landing: false, runway: null, pad: null,
    rollout: 0, gone: false, holding: false, fade: 1, bank: 0, warn: 0,
    spawnedAt: w.elapsed, idleSince: 0,
  };
  w.planes.push(p);
  w.events.push({ t: 'spawn', id: p.id, typeId: type.id });
  return p;
}

/** current airborne cap from the level's governor */
export function trafficCap(w: World): number {
  const lv = w.level;
  return Math.min(lv.capMax, lv.capBase + Math.floor(w.arrivals / lv.capDiv));
}
